// The spend-approval sheet — an admin or executive clearing (or denying) an
// order that crossed the dollar threshold. Extracted from
// PurchasingQueuePage.js at Session 79.14c.
//
// A DENIAL ALSO LANDS ON STATUS 'approved' AND NULLS approved_at, which is why
// ApprovedToOrderBanner keys on approved_at rather than on status. Reading this
// sheet is what makes that separator make sense.
//
// NOT COVERED BY SESSION 79.14c's BEFORE-CAPTURE, and the record says so rather
// than implying otherwise: staging holds zero requests in Pending spend
// approval, so this sheet could not be opened without creating data. Its
// extraction is therefore verified by test and by compile, not by screen.

import React from 'react';
import Overlay from '../Overlay';

export default function ApprovalSheet({ visible, req, isMobile, denyReason, setDenyReason, showDenyBox, setShowDenyBox, approvalSaving, approvalError, approvalDone, onApprove, onDeny, onClose }) {
  if (!visible || !req) return null;
  const item = req.material_request_items?.[0];
  const itemName = item?.is_off_catalog ? (item.off_catalog_description || '—') : (item?.inventory_items?.name || '—');
  const orderTotal = req.ordered_price != null ? parseFloat(req.ordered_price) : null;
  const threshold = req.approval_threshold_amount != null ? parseFloat(req.approval_threshold_amount) : null;

  // #250 / #148 Split C, Session 79.14. Converted to Overlay.js - locked rule 3.
  //
  // The save guard is carried across the same way CancelConfirmSheet's is: this
  // sheet has never let go while an approval is being written, and passing
  // `undefined` for onClose keeps both the backdrop and Escape dead for exactly
  // that window.
  //
  // panelStyle carries the height cap and the column layout, which are not
  // decoration: this sheet has a black header that must stay put and a body that
  // scrolls under it. Without them it would either run off the bottom of the
  // screen or grow a second scrollbar inside the first.
  return (
    <Overlay
      variant={isMobile ? 'sheet' : 'modal'}
      maxWidth={420}
      zIndex={4000}
      onClose={approvalSaving ? undefined : onClose}
      panelStyle={{
        maxHeight: '86vh', display: 'flex', flexDirection: 'column',
        // Longhand since #554 gave the modal branch overflowY/overflowX. A shorthand
        // here would land in the SAME merged style object as those longhands, which
        // is the mix this component's own comment warns about.
        overflowY: 'hidden', overflowX: 'hidden',
        borderRadius: isMobile ? '16px 16px 0 0' : 16, // token, not Overlay's 14 - see MarkOrderedSheet
      }}
    >
      <>
        <div style={{ background: '#0F0F0F', padding: '14px 18px', flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>Purchase approval</div>
          <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 2 }}>{req.request_number} · {itemName}</div>
        </div>

        {approvalDone ? (
          <div style={{ padding: '20px 20px 32px' }}>
            <div style={{ background: '#E8F5E9', borderRadius: 12, padding: 20, border: '1px solid #C8E6C9', textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#2E7D32', marginBottom: 4 }}>Approved</div>
              <div style={{ fontSize: 12, color: '#5C5C5C' }}>Purchasing has been notified. The order still needs to be placed with the vendor.</div>
            </div>
            <button onClick={onClose} style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: '#1D9E75', color: 'white', cursor: 'pointer' }}>Done</button>
          </div>
        ) : (
          <div style={{ padding: '16px 18px 32px', overflowY: 'auto' }}>
            {approvalError && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{approvalError}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              <div style={{ background: '#FBEAF0', border: '1px solid #ED93B1', borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#993556', textTransform: 'uppercase' }}>Order total</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#72243E' }}>{orderTotal != null ? `$${orderTotal.toFixed(2)}` : '—'}</div>
              </div>
              <div style={{ background: '#F9F9F7', border: '1px solid #E8E8E8', borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase' }}>Threshold</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0F0F0F' }}>{threshold != null ? `$${threshold.toFixed(2)}` : '—'}</div>
              </div>
            </div>

            {!showDenyBox ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={onApprove} disabled={approvalSaving}
                  style={{ flex: 1, padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: approvalSaving ? '#E0E0E0' : '#1D9E75', color: approvalSaving ? '#9A9A9A' : 'white', cursor: approvalSaving ? 'not-allowed' : 'pointer' }}>
                  Approve
                </button>
                <button onClick={() => setShowDenyBox(true)} disabled={approvalSaving}
                  style={{ flex: 1, padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: '1px solid #F09595', background: 'white', color: '#A32D2D', cursor: 'pointer' }}>
                  Deny
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase' }}>Reason for the requester</div>
                <textarea value={denyReason} onChange={e => setDenyReason(e.target.value)} placeholder="e.g. use the lower-cost vendor instead"
                  style={{ width: '100%', minHeight: 64, border: '1px solid #E0E0E0', borderRadius: 9, padding: 10, fontSize: 16, boxSizing: 'border-box', resize: 'vertical' }} />
                <button onClick={onDeny} disabled={approvalSaving}
                  style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: approvalSaving ? '#E0E0E0' : '#A32D2D', color: approvalSaving ? '#9A9A9A' : 'white', cursor: approvalSaving ? 'not-allowed' : 'pointer' }}>
                  {approvalSaving ? 'Sending back...' : 'Send back to purchasing'}
                </button>
              </div>
            )}
          </div>
        )}
      </>
    </Overlay>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────
